import { getActionDeadlineDate } from '@/lib/time';
import { fromZonedTime } from 'date-fns-tz';

export interface CalendarArtifacts {
  icsContent: string;
  googleUrl: string;
  outlookUrl: string;
}

export interface CalendarEventData {
  title: string;
  description: string;
  start: Date;
  end: Date;
  location?: string;
  timezone?: string;
}

export function buildCalendarArtifacts({
  title,
  description,
  start,
  end,
  location = 'Phone call',
  timezone = 'America/New_York'
}: CalendarEventData): CalendarArtifacts {
  const icsContent = generateICSContent({
    title,
    description,
    start,
    end,
    location
  });

  const googleUrl = generateGoogleCalendarUrl({
    title,
    description,
    start,
    end,
    location,
    timezone
  });

  const outlookUrl = generateOutlookCalendarUrl({
    title,
    description,
    start,
    end,
    location
  });

  return {
    icsContent,
    googleUrl,
    outlookUrl
  };
}

function generateICSContent({
  title,
  description,
  start,
  end,
  location
}: Omit<CalendarEventData, 'timezone'>): string {
  const now = new Date();
  const uid = `action-center-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Action Center//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatDateICS(now)}`,
    `DTSTART:${formatDateICS(start)}`,
    `DTEND:${formatDateICS(end)}`,
    `SUMMARY:${escapeICS(title)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    `LOCATION:${escapeICS(location)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
}

function generateGoogleCalendarUrl({
  title,
  description,
  start,
  end,
  location,
  timezone
}: CalendarEventData): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatDateUrl(start)}/${formatDateUrl(end)}`,
    details: description,
    location: location || '',
    ctz: timezone || 'America/New_York'
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function generateOutlookCalendarUrl({
  title,
  description,
  start,
  end,
  location
}: Omit<CalendarEventData, 'timezone'>): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    body: description,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    location: location || ''
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function formatDateICS(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function formatDateUrl(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

export function downloadICS(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildContactTitle(contactName: string, contactCompany?: string): string {
  return `Follow up with ${contactName}${contactCompany ? ` - ${contactCompany}` : ''}`;
}

export function buildContactDescription(
  phone?: string,
  email?: string,
  company?: string,
  context?: string,
  campaignName?: string
): string {
  const parts: string[] = [];
  
  if (phone) parts.push(`Phone: ${phone}`);
  if (email) parts.push(`Email: ${email}`);
  if (company) parts.push(`Company: ${company}`);
  if (context) parts.push(`Context: ${context}`);
  if (campaignName) parts.push(`Campaign: ${campaignName}`);
  
  // Add ET timezone note for clarity
  parts.push('');
  parts.push('Note: Times shown in Eastern Time (ET)');
  
  return parts.join('\n');
}

export function buildAppointmentDescriptionSafe(
  phone?: string,
  company?: string,
  purpose: string = 'Follow-up call appointment',
  details?: { service?: string; location?: string; notes?: string }
): string {
  const parts: string[] = [];
  
  if (phone) parts.push(`Phone: ${phone}`);
  if (company) parts.push(`Company: ${company}`);
  parts.push(`Purpose: ${purpose}`);
  
  if (details?.service) parts.push(`Service: ${details.service}`);
  if (details?.location && details.location !== 'Phone call') parts.push(`Location: ${details.location}`);
  if (details?.notes) {
    // Clean notes to remove any system messages
    const cleanNotes = details.notes.replace(/\b(system|agent|tts|pause|rule|instruction|campaign)\b/gi, '').trim();
    if (cleanNotes) parts.push(`Details: ${cleanNotes}`);
  }
  
  // Add ET timezone note for clarity
  parts.push('');
  parts.push('Note: Times shown in Eastern Time (ET)');
  
  return parts.join('\n');
}

export function buildFollowupDescriptionSafe(
  phone?: string,
  company?: string,
  purpose: string = 'Follow-up call'
): string {
  const parts: string[] = [];
  
  if (phone) parts.push(`Phone: ${phone}`);
  if (company) parts.push(`Company: ${company}`);
  parts.push(`Purpose: ${purpose}`);
  
  // Add ET timezone note for clarity
  parts.push('');
  parts.push('Note: Times shown in Eastern Time (ET)');
  
  return parts.join('\n');
}

export function parseResolvedAppointmentToETDate(
  resolvedText: string,
  baseDate: Date = new Date()
): { startDate: Date; endDate: Date } | null {
  if (!resolvedText) return null;

  // Parse "Friday, Sep 12 at 2:00 PM ET" format
  const match = resolvedText.match(
    /(\w+),\s*(\w+)\s+(\d+)\s+at\s+(\d{1,2}):(\d{2})\s*(AM|PM)\s*ET/i
  );
  if (!match) return null;

  const [, _dayName, monthName, day, hours, minutes, ampm] = match;

  // Parse month name to number (support both Sep and Sept)
  const monthMap: { [key: string]: number } = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Sept: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  const monthIndex = monthMap[monthName];
  if (monthIndex === undefined) return null;

  // Convert to 24-hour format
  let hour24 = parseInt(hours, 10);
  if (ampm.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
  if (ampm.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;

  const pad = (n: number) => String(n).padStart(2, '0');

  const buildUtcFromET = (year: number) => {
    const month = monthIndex + 1;
    const isoLocal = `${year}-${pad(month)}-${pad(parseInt(day, 10))}T${pad(hour24)}:${pad(
      parseInt(minutes, 10)
    )}:00`;
    // Interpret the above local time in America/New_York and convert to a UTC Date
    return fromZonedTime(isoLocal, 'America/New_York');
  };

  // Start with current year; if already past, roll to next year
  let startDate = buildUtcFromET(baseDate.getFullYear());
  if (startDate.getTime() < baseDate.getTime()) {
    startDate = buildUtcFromET(baseDate.getFullYear() + 1);
  }

  // Default 30-minute duration
  const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

  return { startDate, endDate };
}

export function getSuggestedStartTime(
  action: any,
  actionType: string,
  createdAt: string,
  callMode: 'inbound' | 'outbound'
): Date {
  // Use manual due date if set, otherwise calculate based on action type
  if (action?.action_data?.due_at) {
    return new Date(action.action_data.due_at);
  }
  
  return getActionDeadlineDate(actionType, createdAt, callMode);
}